import { useEffect } from 'react';
import { type Address, parseEther } from 'viem';
import { useWriteContract, useWaitForTransactionReceipt } from 'wagmi';
import { ERC20_ABI, SWAP_ABI } from '../constants';
import type { SwapStep } from './useFormState';

interface UseSwapActionsParams {
  oldFarToken: Address;
  swapContract: Address;
  setSwapStep: (step: SwapStep) => void;
  setLastSwappedAmount?: (amount: string) => void;
  refetchOldBalance: () => void;
  refetchNewBalance: () => void;
  refetchStats: () => void;
  refetchUserInfo: () => void;
  refetchAllowance: () => void;
}

export const useSwapActions = ({
  oldFarToken,
  swapContract,
  setSwapStep,
  setLastSwappedAmount,
  refetchOldBalance,
  refetchNewBalance,
  refetchStats,
  refetchUserInfo,
  refetchAllowance,
}: UseSwapActionsParams) => {
  // Write contract hooks
  const {
    writeContract: writeApprove,
    data: approveHash,
    isPending: isApprovePending,
    isError: isApproveError,
    error: approveError,
  } = useWriteContract();

  const {
    writeContract: writeSwap,
    data: swapHash,
    isPending: isSwapPending,
    isError: isSwapError,
    error: swapError,
  } = useWriteContract();

  // Wait for approve transaction
  const { isLoading: isApproveLoading, isSuccess: isApproveSuccess } = useWaitForTransactionReceipt({
    hash: approveHash,
  });

  // Wait for swap transaction
  const { isLoading: isSwapLoading, isSuccess: isSwapSuccess } = useWaitForTransactionReceipt({
    hash: swapHash,
  });

  // Auto-refresh after successful transactions
  useEffect(() => {
    if (isApproveSuccess) {
      refetchAllowance();
      setSwapStep('idle');
    }
  }, [isApproveSuccess, refetchAllowance, setSwapStep]);

  useEffect(() => {
    if (isSwapSuccess) {
      refetchOldBalance();
      refetchNewBalance();
      refetchStats();
      refetchUserInfo();
      setSwapStep('idle');
    }
  }, [isSwapSuccess, refetchOldBalance, refetchNewBalance, refetchStats, refetchUserInfo, setSwapStep]);

  // Handle token approval
  const handleApprove = async (swapAmount: string, address: Address | undefined) => {
    if (!swapAmount || !address) return;

    setSwapStep('approving');

    try {
      const amount = parseEther(swapAmount);
      writeApprove({
        address: oldFarToken,
        abi: ERC20_ABI,
        functionName: 'approve',
        args: [swapContract, amount],
      });
    } catch {
      setSwapStep('idle');
    }
  };

  // Handle swap
  const handleSwap = async (swapAmount: string, address: Address | undefined) => {
    if (!swapAmount || !address) return;

    // Save the amount before swap for success display
    setLastSwappedAmount?.(swapAmount);
    setSwapStep('swapping');

    try {
      const amount = parseEther(swapAmount);
      writeSwap({
        address: swapContract,
        abi: SWAP_ABI,
        functionName: 'swap',
        args: [amount],
      });
    } catch {
      setSwapStep('idle');
    }
  };

  return {
    handleApprove,
    handleSwap,
    isApprovePending,
    isApproveLoading,
    isApproveSuccess,
    isApproveError,
    approveError,
    approveHash,
    isSwapPending,
    isSwapLoading,
    isSwapSuccess,
    isSwapError,
    swapError,
    swapHash,
  };
};
